import React from 'react';
import { Grid, TextField, MenuItem, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme, Chip } from '@mui/material';
import { Student } from '@/types';
import { PERFORMANCE_LEVELS, YES_NO } from '../../StudentForm/constants';

interface AcademicSectionProps {
  values: Partial<Student>;
  onChange: (id: keyof Student, value: any) => void;
}

export const AcademicSection: React.FC<AcademicSectionProps> = ({
  values,
  onChange
}) => {
  const theme = useTheme();

  const handleChange = (field: keyof Student) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'number' 
      ? parseFloat(event.target.value) || 0
      : event.target.value;
    onChange(field, value);
  };

  const handleGpaChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange('academicPerformance', {
      ...values.academicPerformance,
      gpa: parseFloat(event.target.value) || 0
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Current GPA"
          value={values.academicPerformance?.gpa ?? ''}
          onChange={handleGpaChange}
          inputProps={{ min: 0, max: 4, step: 0.01 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Credits Earned"
          value={values.academicPerformance?.credits ?? ''}
          onChange={(e) => onChange('academicPerformance', {
            ...values.academicPerformance,
            credits: parseInt(e.target.value) || 0
          })}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          type="number"
          label="Courses Currently Failing"
          value={values.academicPerformance?.coursesFailing ?? ''}
          onChange={(e) => onChange('academicPerformance', {
            ...values.academicPerformance,
            coursesFailing: parseInt(e.target.value) || 0
          })}
          inputProps={{ min: 0 }}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="Previous Performance"
          value={values.previousPerformance ?? ''}
          onChange={handleChange('previousPerformance')}
        >
          {PERFORMANCE_LEVELS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Academic Advisor"
          value={values.academicAdvisor ?? ''}
          onChange={handleChange('academicAdvisor')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Homeroom Teacher"
          value={values.homeroomTeacher ?? ''}
          onChange={handleChange('homeroomTeacher')}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="IEP/504 Plan"
          value={values.iepStatus ?? ''}
          onChange={handleChange('iepStatus')}
        >
          {YES_NO.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Class Schedule
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Period
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Class Name
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Classroom
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Teacher
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Absences
                </TableCell>
                <TableCell 
                  sx={{ 
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontWeight: 500
                  }}
                >
                  Tardies
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {values.schedule?.map((period, index) => {
                // Generate consistent but different numbers for each period using hash
                const hash = period.classroomNumber.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                const absences = hash % 4; // 0-3 absences
                const tardies = (hash * 2) % 3; // 0-2 tardies

                return (
                  <TableRow key={index}>
                    <TableCell>{period.period}</TableCell>
                    <TableCell>{period.className}</TableCell>
                    <TableCell>{period.classroomNumber}</TableCell>
                    <TableCell>{period.teacherName}</TableCell>
                    <TableCell>
                      <Chip
                        label={absences}
                        size="small"
                        color={absences > 1 ? "error" : absences === 1 ? "warning" : "success"}
                        variant={absences === 0 ? "outlined" : "filled"}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tardies}
                        size="small"
                        color={tardies > 1 ? "error" : tardies === 1 ? "warning" : "success"}
                        variant={tardies === 0 ? "outlined" : "filled"}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!values.schedule || values.schedule.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No schedule information available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};